import moment from "moment";
import React from "react";
import { BiCog, BiCopy } from "react-icons/bi";
import { FaWeightHanging } from "react-icons/fa";
import { FiBarChart, FiBell, FiFileText, FiPieChart } from "react-icons/fi";
import { LineWave } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import demoPic from "src/assets/png/alt.png"; // Replace with the actual path to demoPic
import { useStudent } from "src/contexts/student";
import { useClipboard } from "use-clipboard-copy";
import { CenteredDiv } from "../calendar/styling/styles";
import Activities from "../student-info/Activities";
import StudentNotes from "../student-info/Notes";
import { GetStudentStatus } from "../student-info/student-status";
import CopyButton from "./CopyButton";

const StudentInfoAlt: React.FC = () => {
  const { student } = useStudent();

  const navigate = useNavigate();

  const clipboard = useClipboard();

  function GetTraineeLink() {
    if (!student) return;

    clipboard.copy(student.link);
  }

  if (!student)
    return (
      <CenteredDiv>
        <LineWave color="black" width={80} height={80} />
      </CenteredDiv>
    );

  return (
    <div className="h-full overflow-hidden">
      <div className="mx-auto flex h-full max-w-[1600px] flex-col px-2 md:p-6">
        {/* Student Profile Card */}
        <div className="mb-6 flex justify-between border-t bg-transparent bg-white pt-2 shadow-none dark:bg-zinc-800 md:rounded-lg md:border-t-0 md:bg-white md:p-6 md:shadow-md">
          <div className="flex items-center gap-6">
            <img
              className="hidden h-16 w-16 rounded-full object-cover md:flex"
              src={student.photourl ?? demoPic}
              alt={"PROFILE"}
            />

            <div className="flex-col overflow-hidden">
              <h2 className="hidden truncate font-bold text-zinc-800 dark:text-white md:flex">
                {student?.name}
              </h2>
              <p className="truncate text-sm uppercase text-zinc-500">
                {GetStudentStatus(student.status).label}
              </p>
              <p className="truncate text-zinc-500">
                Aluno • desde {moment(student?.created_at).format("LL")}
              </p>
            </div>
          </div>
          <div className="flex flex-col justify-center gap-2">
            <CopyButton
              onClick={GetTraineeLink}
              className="flex h-8 items-center gap-2 rounded-lg bg-zinc-100 bg-zinc-200 p-2 text-sm font-medium text-zinc-700 transition-all hover:bg-zinc-300 md:w-36 md:bg-zinc-200 md:px-4"
            >
              <BiCopy size={24} />
              <p className="hidden w-full text-right uppercase md:flex">
                Copiar Link
              </p>
            </CopyButton>
            <button
              onClick={() => navigate("settings")}
              className="flex h-8 items-center gap-2 rounded-lg bg-zinc-100 bg-zinc-200 p-2 text-sm font-medium text-zinc-700 transition-all hover:bg-zinc-300 md:w-36 md:bg-zinc-200 md:px-4"
            >
              <BiCog size={24} />
              <p className="hidden w-full text-right uppercase md:flex">
                Opções
              </p>
            </button>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="mb-6 flex flex-col gap-2 md:mb-8 md:flex-row md:flex-wrap md:gap-4">
          <button
            onClick={() => navigate("workouts")}
            className="flex min-w-[180px] flex-1 flex-row items-center justify-center gap-1 rounded-lg border-2 border-transparent bg-zinc-100 p-3 text-primary-500 transition-all active:opacity-85 dark:bg-zinc-800 md:flex-col md:gap-2 md:p-4 md:hover:scale-105 md:hover:shadow-lg md:hover:dark:border-zinc-700"
          >
            <FaWeightHanging size={24} />
            <span className="text-sm font-medium">Protocolos</span>
          </button>
          <button
            onClick={() => navigate("assessments")}
            className="flex min-w-[180px] flex-1 flex-row items-center justify-center gap-1 rounded-lg border-2 border-transparent bg-zinc-100 p-3 text-primary-600 transition-all active:opacity-85 dark:bg-zinc-800 md:flex-col md:gap-2 md:p-4 md:hover:scale-105 md:hover:shadow-lg md:hover:dark:border-zinc-700"
          >
            <FiPieChart size={24} />
            <span className="text-sm font-medium">Avaliações</span>
          </button>
          <button
            onClick={() => navigate("progression")}
            className="flex flex-1 flex-row items-center justify-center gap-1 rounded-lg border-2 border-transparent bg-zinc-100 p-3 text-primary-700 transition-all active:opacity-85 dark:bg-zinc-800 md:flex-col md:gap-2 md:p-4 md:hover:scale-105 md:hover:shadow-lg md:hover:dark:border-zinc-700"
          >
            <FiBarChart size={24} />
            <span className="text-sm font-medium">Progressão</span>
          </button>
          <button
            disabled
            className="flex flex-1 flex-row items-center justify-center gap-1 rounded-lg border-2 border-transparent bg-zinc-100 p-3 text-primary-800 transition-all active:opacity-85 dark:bg-zinc-800 md:flex-col md:gap-2 md:p-4 md:hover:scale-105 md:hover:shadow-lg md:hover:dark:border-zinc-700"
          >
            <FiFileText size={24} />
            <span className="text-sm font-medium">Anamnese</span>
          </button>
        </div>

        <div className="flex flex-1 flex-col-reverse gap-6 md:flex-row">
          {/* Pronctuary Section */}
          <div className="flex min-h-[50vh] flex-1 flex-col rounded-lg bg-white p-6 shadow-md dark:bg-zinc-800 md:min-h-0">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex flex-row items-center gap-2">
                <FiFileText className="text-primary-500" />
                <h3 className="text-xl font-semibold uppercase dark:text-white">
                  Anotações
                </h3>
              </div>
            </div>
            <StudentNotes />
          </div>

          {/* Notifications Section */}
          <div className="flex min-h-[50vh] flex-1 flex-col overflow-hidden rounded-lg bg-white p-6 shadow-md dark:bg-zinc-800 md:min-h-0">
            <div className="mb-4 flex items-center gap-2">
              <FiBell className="text-primary-500" />
              <h3 className="text-xl font-semibold uppercase dark:text-white">
                Notificações
              </h3>
            </div>
            <Activities />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentInfoAlt;
